import React from 'react';
import { X, Database, Search } from 'lucide-react';

// Utility: Format database type for display
const formatDbType = (dbType) => {
  if (!dbType) return '';
  const typeMap = {
    'mysql': 'MySQL',
    'postgresql': 'PostgreSQL',
    'mssql': 'MSSQL',
    'snowflake': 'Snowflake'
  };
  return typeMap[dbType.toLowerCase()] || dbType;
};

const DatasourceSelectionModal = ({
  isOpen,
  mode, // 'taurus' or 'virgo'
  onClose,
  onSelect,
  datasources,
  currentSelection,
  searchValue,
  onSearchChange
}) => {
  if (!isOpen) return null;

  const isTaurus = mode === 'taurus';
  const title = isTaurus ? 'Select Datasource for Taurus' : 'Select Datasource for Virgo';

  // Filter datasources based on mode
  const filteredDatasources = datasources
    .filter(ds => isTaurus ? ds.is_editable : true) // Taurus: only editable, Virgo: all
    .filter(ds =>
      ds.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      ds.db_type.toLowerCase().includes(searchValue.toLowerCase())
    );

  const hasNoDatasources = datasources.filter(ds => isTaurus ? ds.is_editable : true).length === 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="h-6 w-6 text-white" />
            <h2 className="text-xl font-semibold text-white">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-500 dark:hover:bg-blue-600 rounded-lg p-1 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {hasNoDatasources ? (
            // No datasources available
            <div className="text-center py-8">
              <Database className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {isTaurus
                  ? "No datasources available. Add a datasource in Settings to use Taurus mode."
                  : "No datasources available. Add a datasource in Settings."}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search datasources..."
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Datasource List */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredDatasources.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      No datasources found matching "{searchValue}"
                    </p>
                  </div>
                ) : (
                  filteredDatasources.map((ds) => (
                    <button
                      key={ds.name}
                      onClick={() => onSelect(ds)}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                        currentSelection?.name === ds.name
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-800 dark:text-gray-200">{ds.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{formatDbType(ds.db_type)}</div>
                        </div>
                        {currentSelection?.name === ds.name && (
                          <div className="text-blue-600 dark:text-blue-400">✓</div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatasourceSelectionModal;
